import * as React from 'react';
import DataGrid, { Column, Editing, Scrolling, GroupPanel, Sorting, SearchPanel, LoadPanel, Button, HeaderFilter, Paging } from 'devextreme-react/data-grid';
import { withStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import MoreIcon from '@markinson/uicomponents-v2/SvgIcons/More';
import classNames from 'classnames';

import 'devextreme/dist/css/dx.common.css';
import 'devextreme/dist/css/dx.light.css';

import { IKitComponentsLinesProps } from './KitComponentsLines.properties';
import styles from './KitComponentsLines.styles';
import { COLUMN_SIZE, DEFAULT_PAGE_SIZE } from 'components/common/DataGridDevEx/DataGrid.constants';
import ProductCell from 'components/common/ProductCell';
import { ICalculateComponentDetailsFacade, IWIPComponentFacade } from 'api/swaggerTypes';
import { useGridKeyboardNavigation } from 'components/common/DataGridDevEx/DataGrid.hooks';
import { showSnackBar } from 'components/common/SnackBars/SnackBar.hooks';
import { MODULES } from '../KitComponents.constants';

const NumberOfDecimalPlaces = 2;

function KitComponentsLines(props: Readonly<IKitComponentsLinesProps>): JSX.Element {
    const {
        componentLinesDataSource, classes, className, style, worksaleLineId, disableSuppliedQuantityEditing, forwardedRef, componentLinesDirty,
        showDetailOnClick, onDelete, onComponentLineFieldChanged, onEditChange, setComponentLinesDirty, module
    } = props;
    const { preventDataGridDefaults } = useGridKeyboardNavigation();
    const dataGridRef = React.useRef<DataGrid>();
    const [editState, setEditState] = React.useState(false);
    const [initialKitComponents, setOriginalState] = React.useState([]);

    React.useImperativeHandle(
        forwardedRef,
        () => ({
            componentLinesBreadcrumbHandler(): boolean {
                if (componentLinesDirty) {
                    showSnackBar({ variant: 'warning', message: 'Please save or discard changes.' });
                }

                return componentLinesDirty;
            },
        }),
        [componentLinesDirty]
    );

    async function handleSetCellValue<K extends keyof IWIPComponentFacade>(prevData: IWIPComponentFacade, newData: IWIPComponentFacade, field: K, value: any): Promise<void> {
        if (onComponentLineFieldChanged) {
            const mergedData = { ...prevData, ...newData };
            mergedData[field] = value;
            const response = await onComponentLineFieldChanged(worksaleLineId, mergedData.ComponentId, mergedData, field as keyof ICalculateComponentDetailsFacade);
            const lineData = response ? response : mergedData;
            newData.RequiredQuantity = lineData?.RequiredQuantity;
            newData.SuppliedQuantity = lineData?.SuppliedQuantity;
            newData.ReturnQuantity = (lineData as any)?.ReturnQuantity;
            newData.Description = lineData?.Description;
            newData.ProductId = lineData?.ProductId;
            setComponentLinesDirty(true);
            newData.OnOrderQuantity = lineData?.OnOrderQuantity;
        }
    }

    async function deleteClick(e: any): Promise<void> {
        if (onDelete) {
            onDelete(Number(e.row.key));
        }
    }

    const onEditorPreparing = React.useCallback(
        (e) => {
            e.editorOptions.onFocusIn = (args) => {
                const input = args.element.querySelector("input[type='text']");
                if (input) {
                    input.select();
                }
            };
            if (e.dataField === 'SuppliedQuantity') {
                e.editorOptions.disabled = disableSuppliedQuantityEditing || e.row?.data?.SuppliedQuantityReadOnly;
            }
        },
        [disableSuppliedQuantityEditing]
    );

    React.useEffect(
        () => {
            const items = dataGridRef?.current?.instance?.getDataSource()?.items();

            if (!items) return;

            if (items.length && initialKitComponents.length === 0) {
                setOriginalState(items.map((item) => ({ ...item })));

                return;
            }

            if (
                initialKitComponents.length !== 0 &&
                items.length !== initialKitComponents.length
            ) {
                setComponentLinesDirty(true);
            }
        },
        [dataGridRef, initialKitComponents.length]
    );

    React.useEffect(
        () => {
            if (!editState) return;

            const items = dataGridRef?.current?.instance?.getDataSource()?.items();

            if (!items || !initialKitComponents.length) {
                setEditState(false);

                return;
            }

            const keys = ['ComponentId', 'ProductId', 'RequiredQuantity', 'SuppliedQuantity', 'ReturnQuantity'];

            const hasDifference = items.some((obj) => {
                const original = initialKitComponents.find((c) => c.ComponentId === obj.ComponentId);

                if (!original) return true;

                return keys.some((key) => obj[key] !== original[key]);
            });

            setComponentLinesDirty(hasDifference);
            setEditState(false);
        },
        [editState, initialKitComponents, dataGridRef]
    );

    const detailCellComponent = React.useCallback(
        (allProps) => {
            return <MoreIcon className={classes.lineDetailIcon} onClick={() => {
                if (showDetailOnClick) {
                    showDetailOnClick(allProps.data ? allProps.data.data : null);
                }
            }} />;
        },
        [classes, showDetailOnClick]
    );

    return (
        <Paper className={classNames(classes.parentContainer, className)} style={style}>
            <DataGrid
                className={classes.worksaleLineDataGrid}
                onKeyDown={preventDataGridDefaults}
                dataSource={componentLinesDataSource}
                showBorders={false}
                renderAsync={true}
                repaintChangesOnly={true}
                allowColumnReordering={true}
                remoteOperations={true}
                allowColumnResizing={true}
                columnResizingMode={'nextColumn'}
                hoverStateEnabled={true}
                noDataText=''
                ref={dataGridRef}
                onEditorPreparing={onEditorPreparing}
                onEditCanceled={() => { onEditChange(false); }}
                onEditingStart={() => onEditChange(true)}
                onSaved={
                    () => {
                        onEditChange(false);
                        setEditState(true);
                    }
                }
                onRowUpdated={() => {
                    setComponentLinesDirty(true);
                }}
            >
                <Paging defaultPageSize={DEFAULT_PAGE_SIZE} defaultPageIndex={0} />
                <Editing
                    refreshMode={'reshape'}
                    mode='row'
                    allowAdding={false}
                    allowDeleting={true}
                    allowUpdating={true}
                    confirmDelete={false}
                />
                <HeaderFilter
                    visible={true}
                />
                <Column
                    allowEditing={false}
                    minWidth={COLUMN_SIZE.sm0}
                    width={COLUMN_SIZE.sm1}
                    allowResizing={false}
                    fixed={true}
                    alignment='center'
                    allowHeaderFiltering={false}
                    cellComponent={detailCellComponent}
                />
                <Column
                    dataField={'ProductIdDisplay'}
                    caption={'Product'}
                    allowEditing={false}
                    allowHeaderFiltering={false}
                    minWidth={COLUMN_SIZE.md0}
                    width={COLUMN_SIZE.md1}
                    fixed={true}
                    cellComponent={(allProps) => (
                        <ProductCell
                            urlParamKeys={['ProductId']}
                            toLink={'/inventory-enquiry/product-details'}
                            {...allProps} />
                    )}
                />
                <Column
                    dataField={'ProductGroup'}
                    caption={'Group'}
                    allowEditing={false}
                    allowHeaderFiltering={false}
                    minWidth={COLUMN_SIZE.md0}
                    width={COLUMN_SIZE.md1}
                    editorOptions={{
                        step: 0
                    }} />
                <Column
                    dataField={'RequiredQuantity'}
                    caption={'Required'}
                    allowHeaderFiltering={false}
                    minWidth={COLUMN_SIZE.md0}
                    width={COLUMN_SIZE.md1}
                    editorOptions={{
                        step: 0
                    }}
                    setCellValue={async (newData: ICalculateComponentDetailsFacade, value: number, prevData: ICalculateComponentDetailsFacade): Promise<void> => {
                        await handleSetCellValue(prevData, newData, 'RequiredQuantity', value);
                    }}
                    calculateDisplayValue={(data: Record<string, unknown>) => (data?.RequiredQuantity as number)?.toFixed(NumberOfDecimalPlaces)} />
                {
                    module !== MODULES.CreditNotes ? (
                        <Column
                            dataField={'SuppliedQuantity'}
                            caption={'Supplied'}
                            allowHeaderFiltering={false}
                            minWidth={COLUMN_SIZE.md0}
                            width={COLUMN_SIZE.md1}
                            editorOptions={{
                                step: 0
                            }}
                            setCellValue={async (newData: ICalculateComponentDetailsFacade, value: number, prevData: ICalculateComponentDetailsFacade): Promise<void> => {
                                await handleSetCellValue(prevData, newData, 'SuppliedQuantity', value);
                            }}
                            calculateDisplayValue={(data: Record<string, unknown>) => (data?.SuppliedQuantity as number)?.toFixed(NumberOfDecimalPlaces)}
                        />
                    ) : (
                        <Column
                            dataField={'ReturnQuantity'}
                            caption={'Return'}
                            allowHeaderFiltering={false}
                            minWidth={COLUMN_SIZE.md0}
                            width={COLUMN_SIZE.md1}
                            editorOptions={{
                                step: 0
                            }}
                            setCellValue={async (newData: ICalculateComponentDetailsFacade, value: number, prevData: ICalculateComponentDetailsFacade): Promise<void> => {
                                await handleSetCellValue(prevData, newData, 'ReturnQuantity', value);
                            }}
                            calculateDisplayValue={(data: Record<string, unknown>) => (data?.ReturnQuantity as number)?.toFixed(NumberOfDecimalPlaces)}
                        />
                    )
                }
                <Column
                    dataField={'Description'}
                    caption={'Description'}
                    allowHeaderFiltering={false}
                    allowEditing={false}
                    editorOptions={{
                        disabled: true,
                        maxLength: 40
                    }}
                    minWidth={COLUMN_SIZE.lg1} />
                <Column dataField={'OnOrderQuantity'} visible={false} />
                <Column type={'buttons'} width={COLUMN_SIZE.sm1} allowResizing={false} fixed={true}>
                    <Button name={'save'} icon={'save'} />
                    <Button name={'edit'} icon={'edit'} />
                    <Button name={'delete'} icon={'trash'} onClick={deleteClick} />
                    <Button name={'cancel'} icon={'undo'} />
                    <Button name={'undelete'} icon={'undo'} />
                </Column>
                <Sorting mode='single' />
                <GroupPanel visible={false} />
                <SearchPanel visible={false} />
                <Scrolling mode={'infinite'} />
                <LoadPanel enabled={false} />
            </DataGrid>
        </Paper >
    );
}

export default withStyles(styles, { index: 1 })(React.memo(KitComponentsLines));
